import { uploadImage } from '../../../api/cardConfigApi'
import { getCouponTemplatesSuggestions } from '../../../api/couponApi'
import {
  createSalesCampaign,
  editSalesCampaign,
  getSalesCampaignDetail,
} from '../../../api/salesCampaignApi'
import DebounceSelect from '../../../components/debounce-select/debounce-select'
import config from '../../../config'
import { getImageUrlToUploadFileInput } from '../../../helpers/image-upload-helper'
import { getImageUrl, getOptions } from '../../../helpers/utils'
import translate from '../../../i18n'
import { request } from '../../../request'
import { OptionType, UploadFileDirectoryEnum } from '../../../share-types'
import { ReduxStore } from '../../../store'
import { Plan } from '../../../store/global/reducer'
import { SalesCampaignStatusOptions } from '../constant'
import { ISalesCampaignFormData } from '../share-type'
import { UploadOutlined } from '@ant-design/icons'
import { Button, DatePicker, Form, Input, notification, Radio, Select, Switch, Upload } from 'antd'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { compose } from 'redux'

const { Option } = Select

const SalesCampaignForm = (props: ConnectedProps<typeof withConnect>) => {
  const [form] = Form.useForm()

  const params = useParams()
  const navigate = useNavigate()
  const [salesCampaignId] = useState<string | undefined>(params?.id)
  const [defaultCouponTemplateOptions, setDefaultCouponTemplateOptions] = useState<OptionType[]>([])
  const enableCouponReminder = Form.useWatch('enableCouponReminder', form)

  useEffect(() => {
    fetchCouponTemplatesSuggestions('').then(options => setDefaultCouponTemplateOptions(options))
  }, [])

  useEffect(() => {
    if (salesCampaignId) {
      getSalesCampaignDetail(Number(salesCampaignId)).then(res => {
        form.setFieldsValue({
          ...res,
          startDatetime: dayjs(res.startDatetime),
          endDatetime: dayjs(res.endDatetime),
          floatingPortalUrl: res.floatingPortalUrl
            ? getImageUrlToUploadFileInput([res.floatingPortalUrl])
            : null,
          chargeBannerUrl: res.chargeBannerUrl
            ? getImageUrlToUploadFileInput([res.chargeBannerUrl])
            : null,
        })
        if (res.couponTemplateId) {
          setDefaultCouponTemplateOptions(
            getOptions([{ name: res.couponTemplateName || '', id: res.couponTemplateId }]),
          )
        }
      })
    }
  }, [salesCampaignId])

  useEffect(() => {
    if (!enableCouponReminder) {
      form.setFieldValue('couponTemplateId', null)
    }
  }, [enableCouponReminder])

  const onFinish = (formData: ISalesCampaignFormData) => {
    if (formData.floatingPortalUrl) {
      formData.floatingPortalUrl = getImageUrl(
        'floatingPortalUrl',
        `${config.assetUrl}/${UploadFileDirectoryEnum.HOMEPAGE_SALES_CAMPAIGNS}`,
        formData.floatingPortalUrl,
      )
    } else {
      formData.floatingPortalUrl = null
    }
    if (formData.chargeBannerUrl) {
      formData.chargeBannerUrl = getImageUrl(
        'chargeBannerUrl',
        `${config.assetUrl}/${UploadFileDirectoryEnum.CHARGE_SALES_CAMPAIGNS}`,
        formData.chargeBannerUrl,
      )
    } else {
      formData.chargeBannerUrl = null
    }
    formData.startDatetime = dayjs(formData.startDatetime).utc().format()
    formData.endDatetime = dayjs(formData.endDatetime).utc().format()

    let promise
    if (salesCampaignId) {
      promise = editSalesCampaign(Number(salesCampaignId), formData).then(res => {
        notification.success({
          message: translate('success'),
        })
        navigate(`/sales-campaigns/${res.id}`)
      })
    } else {
      promise = createSalesCampaign(formData).then(res => {
        notification.success({
          message: translate('success'),
        })
        navigate(`/sales-campaigns/${res.id}`)
      })
    }
    promise.catch(error => {
      notification.error({
        message: translate('apiError'),
        description: error.message ?? '',
      })
    })
  }

  const normFile = (e: any) => {
    if (Array.isArray(e)) {
      return e
    }
    return e?.fileList
  }

  const uploadUrl = (options: any, directory: UploadFileDirectoryEnum) => {
    const { onSuccess, onError, file, filename } = options
    const fileExtension = file.name.split('.').pop()
    const imageFilename = `${filename}_${file.uid}.${fileExtension}`
    uploadImage(imageFilename, directory)
      .then(res => {
        onSuccess(res)
        request({
          url: res,
          method: 'PUT',
          data: file,
          headers: {
            'x-amz-acl': 'public-read',
          },
        })
      })
      .catch(err => {
        onError({ err })
      })
  }

  async function fetchCouponTemplatesSuggestions(
    keyword: string,
  ): Promise<{ label: string; value: number }[]> {
    return getCouponTemplatesSuggestions({ keyword: keyword ?? '', isValid: true }).then(res => {
      return getOptions(res)
    })
  }

  return (
    <Form
      name="sales-campaign-form"
      labelCol={{ span: 6 }}
      wrapperCol={{ span: 12 }}
      onFinish={onFinish}
      autoComplete="off"
      form={form}
      className="resource-form"
      initialValues={{
        enableCouponReminder: false,
      }}
    >
      <Form.Item
        name="name"
        label={translate('name')}
        rules={[{ required: true, message: translate('name.message') }]}
      >
        <Input />
      </Form.Item>
      <Form.Item label={translate('salesCampaign.plan')} name="planId">
        <Select allowClear>
          {props.plans.map((item: Plan) => {
            const translation = item.translations.find(t => t.locale === props.locale)
            return (
              <Option key={item.id} value={item.id}>
                ID: {item.id} - {translation?.name} - ¥{item.price / 100} - {item.credits}点
              </Option>
            )
          })}
        </Select>
      </Form.Item>
      <Form.Item
        name="enableCouponReminder"
        label={translate('salesCampaign.enableCouponReminder')}
        valuePropName="checked"
      >
        <Switch />
      </Form.Item>
      <Form.Item
        label={translate('salesCampaign.couponTemplate')}
        name="couponTemplateId"
        rules={[{ required: enableCouponReminder }]}
      >
        <DebounceSelect
          showSearch
          fetchOptions={fetchCouponTemplatesSuggestions}
          defaultOptions={defaultCouponTemplateOptions}
          allowClear
          disabled={!enableCouponReminder}
        />
      </Form.Item>
      <Form.Item
        name="floatingPortalUrl"
        label={translate('salesCampaign.floatingPortal')}
        valuePropName="fileList"
        getValueFromEvent={normFile}
      >
        <Upload
          maxCount={1}
          name="floatingPortalUrl"
          customRequest={config =>
            uploadUrl(config, UploadFileDirectoryEnum.HOMEPAGE_SALES_CAMPAIGNS)
          }
          listType="picture"
        >
          <Button icon={<UploadOutlined />}>{translate('clickToUpload')}</Button>
        </Upload>
      </Form.Item>
      <Form.Item
        name="chargeBannerUrl"
        label={translate('salesCampaign.chargeBanner')}
        valuePropName="fileList"
        getValueFromEvent={normFile}
      >
        <Upload
          maxCount={1}
          name="chargeBannerUrl"
          customRequest={config =>
            uploadUrl(config, UploadFileDirectoryEnum.CHARGE_SALES_CAMPAIGNS)
          }
          listType="picture"
        >
          <Button icon={<UploadOutlined />}>{translate('clickToUpload')}</Button>
        </Upload>
      </Form.Item>
      <Form.Item name="status" label={translate('status')} rules={[{ required: true }]}>
        <Radio.Group>
          {SalesCampaignStatusOptions.map(option => (
            <Radio value={option.value} key={option.value}>
              {option.label}
            </Radio>
          ))}
        </Radio.Group>
      </Form.Item>
      <Form.Item
        name="startDatetime"
        label={translate('salesCampaign.startDatetime')}
        rules={[{ required: true }]}
      >
        <DatePicker showTime />
      </Form.Item>
      <Form.Item
        name="endDatetime"
        label={translate('salesCampaign.endDatetime')}
        rules={[{ required: true }]}
      >
        <DatePicker showTime />
      </Form.Item>
      <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
        <Button type="primary" htmlType="submit">
          {translate('submit')}
        </Button>
      </Form.Item>
    </Form>
  )
}

const mapStateToProps = (state: ReduxStore) => {
  return {
    locale: state.global.locale,
    plans: state.global.plans,
    couponTemplates: state.couponTemplate.couponTemplates,
  }
}

const withConnect = connect(mapStateToProps)

export default compose(withConnect)(SalesCampaignForm)
